/* Core & libraries */
import React, { useState, useEffect } from "react";
import { useTheme } from "react-jss";
import {defineMessages, FormattedMessage, useIntl} from "react-intl";
import { useNavigate } from "react-router";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useLocation } from "react-router-dom"
import Popover from 'react-bootstrap/Popover';
import { OverlayTrigger } from 'react-bootstrap';

/* Helpers, constants, custom methods, internal libraries */
import {
  REPORTS_ROUTE,
  REPORTS_BOOKING_ROUTE,
  REPORTS_MILEAGE_ROUTE,
  REPORTS_RECEIPTS_ROUTE,
  REPORTS_BOOKING_SUMMARY,
  REPORTS_FLIGHT_SEGMENTS_ROUTE,
  REPORTS_TICKETS_ROUTE,
  REPORTS_COUPONS_ROUTE,
} from '../../../constants/routes';
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useUserContext } from '../../../contexts/userContext';
import { AIRLINES } from '../../../constants/appConstants';


/* Components */
import BookingsTab from "./bookingsTab";
import MileageTab from "./mileageTab";
import ReceiptsTab from "./receiptsTab";
import FlightSegmentsTab from "./flightSegmentsTab";
import UCSelect from "../../ucselect";
import UCButton from '../../buttons';
import RetroModal from '../../aaRetro';
import SummaryTab from './summaryTab';
import TicketTab from './ticketTab';
import CouponsTab from './couponsTab';
import { ReactComponent as FolderQuestionIconSvg } from "../../../resources/images/folder-question.svg";

/* Styles */
import styles, {tabsSelectOpen} from "./styles.js"
import generalStyles from '../../../resources/styles/general';



const ReportsTabbedNav = () => {

  const location = useLocation();

  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { checkPermission } = useHasPermission();
  const userDetails = useUserContext();
  const [showRetroModal, setShowRetroModal] = useState(false);
  const general = generalStyles({ theme });
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const showSummaryTab = checkPermission("F_VIEW_REPORT_SUMMARY_POWERBI") && providerCode === AIRLINES.AA
  const getDefaultPage = () => {
    if(location.pathname.includes(REPORTS_BOOKING_SUMMARY)) return "summary"
    if(location.pathname.includes(REPORTS_BOOKING_ROUTE)) return "bookings"
    if(location.pathname.includes(REPORTS_MILEAGE_ROUTE)) return "mileage"
    if(location.pathname.includes(REPORTS_RECEIPTS_ROUTE)) return "receipts"
    if(location.pathname.includes(REPORTS_FLIGHT_SEGMENTS_ROUTE)) return "flight-segments"
    if(location.pathname.includes(REPORTS_TICKETS_ROUTE)) return "tickets"
    if(location.pathname.includes(REPORTS_COUPONS_ROUTE)) return "coupons"
    if(location.pathname.includes(REPORTS_ROUTE) ) {
      return showSummaryTab ? "summary" : "bookings"
    }
  }
  const [defaultPage, setDefaultPage] = useState(() => getDefaultPage() || (showSummaryTab ? "summary" : "bookings"));


  useEffect(() => {
    setDefaultPage(getDefaultPage())
  }, [location.pathname]);

  const tabOptions = [
    ...showSummaryTab ?
      [{value: 'summary', label: <FormattedMessage id="reports.summary.title"/> }] : [],
    ...(checkPermission("F_VIEW_REPORT_BOOKINGS") ?
      [{value: 'bookings', label: <FormattedMessage id="bookings.title" /> }] : []),
    ...(checkPermission("F_VIEW_REPORT_AWARDS") ?
      [{value: 'mileage', label: <FormattedMessage id={`reports.awards.title.${providerCode}`} /> }] : []),
    ...(checkPermission("F_VIEW_REPORTS_FLIGHTSEGMENTS") ?
      [{value: 'flight-segments', label: <FormattedMessage id="reports.flightSegments.title" /> }] : []),
    ...(checkPermission("F_VIEW_REPORT_STATEMENTS") ?
      [{value: 'receipts', label: <FormattedMessage id="receipts.title" /> }] : []),
    ...(checkPermission("F_VIEW_REPORTS_TICKETS") ?
      [{value: 'tickets', label: <FormattedMessage id="tickets.title" /> }] : []),
    ...(checkPermission("F_VIEW_REPORTS_COUPONS") ?
      [{value: 'coupons', label: <FormattedMessage id="coupon.title" /> }] : []),
  ]

  const getDefaultLabel = () => {
    if(location.pathname.includes(REPORTS_BOOKING_SUMMARY)) return <FormattedMessage id="reports.summary.title"/>
    if(location.pathname.includes(REPORTS_BOOKING_ROUTE)) return <FormattedMessage id="bookings.title"/>
    if(location.pathname.includes(REPORTS_MILEAGE_ROUTE)) return <FormattedMessage id={`reports.awards.title.${providerCode}`}/>
    if(location.pathname.includes(REPORTS_RECEIPTS_ROUTE)) return <FormattedMessage id="receipts.title"/>
    if(location.pathname.includes(REPORTS_FLIGHT_SEGMENTS_ROUTE)) return <FormattedMessage id="reports.flightSegments.title"/>
    if(location.pathname.includes(REPORTS_TICKETS_ROUTE)) return <FormattedMessage id="tickets.title" />
    if(location.pathname.includes(REPORTS_COUPONS_ROUTE)) return <FormattedMessage id="coupon.title" />
    if(location.pathname.includes(REPORTS_ROUTE)) {
      return showSummaryTab ? <FormattedMessage id="reports.summary.title" /> : <FormattedMessage id="bookings.title" />;
    }
  }

  const handleTabChange = nextTab => {
    switch(nextTab) {
      case "summary":
        navigate('../' + REPORTS_BOOKING_SUMMARY);
        break;
      case "bookings":
        navigate('../' + REPORTS_BOOKING_ROUTE);
      break;
      case "mileage":
        navigate('../' + REPORTS_MILEAGE_ROUTE)
      break;
      case "receipts":
        navigate('../' + REPORTS_RECEIPTS_ROUTE)
      break;
      case "flight-segments":
        navigate('../' + REPORTS_FLIGHT_SEGMENTS_ROUTE)
      break;
      case "tickets":
        navigate('../' + REPORTS_TICKETS_ROUTE)
      break;
      case "coupons":
        navigate('../' + REPORTS_COUPONS_ROUTE)
      break;
    }
  }

  const viewTab = tab => {
    switch(tab) {
      case "summary":
        return <SummaryTab />
      case "bookings":
        return <BookingsTab />
      case "mileage":
        return <MileageTab />
      case "receipts":
        return <ReceiptsTab />
      case "flight-segments":
        return <FlightSegmentsTab />
      case "tickets":
        return <TicketTab />
      case "coupons":
        return <CouponsTab />
    }
  }

  const renderInfoMissingData = () => {
    return(
      <Popover className={classes.infoPopover}>
        <div className={classes.infoPopoverDiv}>
          <div className={classes.infoTitle}>
            <FormattedMessage id="reports.tooltip.title" />
          </div>
          <div className={classes.infoDescription}>
            <FormattedMessage id="reports.tooltip.description" />
          </div>
        </div>
      </Popover>
    )
  }

  return (
    <div className={classes.reportsTabbedNav}>
      {isMobile ? (
        <>
          <UCSelect
            styles={{ ...tabsSelectOpen }}
            isMenuOpen = {true}
            defaultValue={{
              value: getDefaultPage() || defaultPage,
              label: getDefaultLabel()
            }}
            options={tabOptions}
            onChange={(tab) => handleTabChange(tab.value)}
          />
          {
            checkPermission("F_RETRO_REWARDS_VIEW") &&
            <UCButton
              onClick={() => setShowRetroModal(true)}
              type="default"
              text={<FormattedMessage id="reports.missingMiles"/>}
              className={classes.missingAwardButton}
            />
          }
          { providerCode === AIRLINES.UC &&
            <div className={classes.missingAwardButton} >
              <OverlayTrigger
                placement="right"
                //show={true}
                overlay={renderInfoMissingData()}
              >
                <div className={classes.reportInfo}>
                  <FolderQuestionIconSvg className={classes.iconStyle} />
                  <p> <FormattedMessage id="choice.reports.tooltip.text" /> </p>
                </div>
              </OverlayTrigger>
            </div>
          }
          {viewTab(defaultPage || getDefaultPage())}
        </>
      ) : (
        <div className="position-relative">
          <Tabs
            onSelect={nextTab => handleTabChange(nextTab)}
            defaultActiveKey={ getDefaultPage() }
            activeKey={defaultPage}
            id="reports-page-tabs"
            className={ classes.tabs }
          >
            {
              showSummaryTab &&
              <Tab eventKey="summary" title={<FormattedMessage id="reports.summary.title" />}>
                { getDefaultPage() === "summary" && <SummaryTab />}
              </Tab>
            }
            {
              checkPermission("F_VIEW_REPORT_BOOKINGS") &&
              <Tab eventKey="bookings" title={<FormattedMessage id="bookings.title" />}>
                { getDefaultPage() === "bookings" && <BookingsTab />}
              </Tab>
            }
            {
              checkPermission("F_VIEW_REPORT_AWARDS") &&
              <Tab eventKey="mileage" title={<FormattedMessage id={`reports.awards.title.${providerCode}`} />}>
                { getDefaultPage() === "mileage" && <MileageTab />}
              </Tab>
            }
            {
              checkPermission("F_VIEW_REPORTS_FLIGHTSEGMENTS") &&
              <Tab eventKey="flight-segments" title={<FormattedMessage id="reports.flightSegments.title" />}>
                { getDefaultPage() === "flight-segments" && <FlightSegmentsTab /> }
              </Tab>
            }
            {
              checkPermission("F_VIEW_REPORT_STATEMENTS") &&
              <Tab eventKey="receipts" title={<FormattedMessage id="receipts.title" />}>
                { getDefaultPage() === "receipts" && <ReceiptsTab /> }
              </Tab>
            }
            {
              checkPermission("F_VIEW_REPORTS_TICKETS") &&
              <Tab eventKey="tickets" title={<FormattedMessage id="tickets.title" />}>
                { getDefaultPage() === "tickets" && <TicketTab /> }
              </Tab>
            }
            {
              checkPermission("F_VIEW_REPORTS_COUPONS") &&
              <Tab eventKey="coupons" title={<FormattedMessage id="coupon.title" />}>
                { getDefaultPage() === "coupons" && <CouponsTab /> }
              </Tab>
            }
          </Tabs>
          {
            checkPermission("F_RETRO_REWARDS_VIEW") &&
            <UCButton
              onClick={() => setShowRetroModal(true)}
              type="default"
              text={<FormattedMessage id="reports.missingMiles"/>}
              className={classes.missingAwardButton}
            />
          }
          { providerCode === AIRLINES.UC &&
            <div className={classes.missingAwardButton} >
              <OverlayTrigger
                placement="left"
                //show={true}
                overlay={renderInfoMissingData()}
              >
                <div className={classes.reportInfo}>
                  <FolderQuestionIconSvg className={classes.iconStyle} />
                  <p> <FormattedMessage id="choice.reports.tooltip.text" /> </p>
                </div>
              </OverlayTrigger>
            </div>
          }
        </div>
      )}
      <RetroModal
        showModal={showRetroModal}
        setShowModal={setShowRetroModal}
      />
    </div>
  )
}

export default ReportsTabbedNav;
